import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import { useCatalog } from "@/api/catalog";
import type {
  Catalog,
  CurrentUser,
  ExpenseEntry,
  ExpensePolicy,
  PersonBrief,
  PolicyIssue,
  ReportDetail,
  ReportSummary,
} from "@/api/types";
import { Icon, type IconName } from "@/components/Icon";
import { HelpText } from "@/components/HelpText";
import { CornerTip } from "@/components/CornerTip";
import { devPreview } from "@/features";
import { type HelpKey, useHelp, useShowTips } from "@/help";
import { ReceiptPreview } from "@/components/ReceiptPreview";
import { Sheet } from "@/components/Sheet";
import { TypeNotice } from "@/components/TypeNotice";
import { StatusBadge } from "@/components/StatusBadge";
import {
  dateRange,
  entryTypeLabel,
  findType,
  eventNote,
  addedHow,
  eventLabel,
  isSentBack,
  longDate,
  miles,
  money,
  nameList,
  relativeTime,
  shortDate,
  todayIso,
} from "@/format";
import type { ReceiptVia } from "@/receiptFiles";
import { PendingRow } from "@/components/PendingExpenses";
import { ExpenseForm, MileageForm, ReceiptForm } from "@/pages/report/EntryForms";
import { useLiveReport } from "@/live";
import { AddPanel } from "@/pages/report/AddPanel";
import { ReceiptDropZone } from "@/pages/report/ReceiptDropZone";
import { useDialogs } from "@/components/dialogs";

export function ReportPage({ user }: { user: CurrentUser }) {
  const { reportId = "" } = useParams();
  const { data, error, loading, reload } = useApiQuery<ReportDetail>(
    reportId ? `/api/expenses/reports/${reportId}` : null,
  );
  const { data: policy } = useApiQuery<ExpensePolicy>("/api/expenses/policy");
  // A mutation answers with the updated report, which is shown at once instead of refetching.
  const [latest, setLatest] = useState<ReportDetail>();
  const report = latest?.id === reportId ? latest : data;

  if (loading && !report) return <ReportSkeleton />;
  if (error && !report) {
    return (
      <div className="stack">
        <BackLink to="/" />
        <div className="error" role="alert">
          {error.message}
        </div>
      </div>
    );
  }
  if (!report) return null;

  return (
    <ReportBody
      report={report}
      policy={policy}
      user={user}
      onReport={setLatest}
      onReload={() => {
        setLatest(undefined);
        reload();
      }}
    />
  );
}

type AddStep = "choose" | "receipt" | "mileage" | "manual" | "pending";
type Review = {
  stage: "manager" | "payroll" | "executive" | "reopen" | "delegator";
  approve: boolean;
};

/** Statuses in which a report is with payroll to pay. */
const WithPayroll = ["ManagerApproved", "AccountingApproved", "ExecutiveApproved"];

/** Where each review posts. */
function reviewPath(reportId: string, review: Review): string {
  if (review.stage === "payroll") return `/api/expenses/reports/${reportId}/payroll/reject`;
  if (review.stage === "reopen") return `/api/expenses/reports/${reportId}/payroll/reopen`;
  if (review.stage === "executive")
    return `/api/expenses/reports/${reportId}/executive/${review.approve ? "approve" : "decline"}`;
  if (review.stage === "delegator")
    return `/api/expenses/reports/${reportId}/delegator/${review.approve ? "approve" : "reject"}`;
  return `/api/expenses/reports/${reportId}/manager/${review.approve ? "approve" : "reject"}`;
}

function ReportBody({
  report,
  policy,
  user,
  onReport,
  onReload,
}: {
  report: ReportDetail;
  policy: ExpensePolicy | undefined;
  user: CurrentUser;
  onReport: (report: ReportDetail) => void;
  onReload: () => void;
}) {
  const api = useApi();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<string>();
  const [adding, setAdding] = useState<AddStep>();
  // Photos, pastes and files from the add panel join the drop zone's queue.
  const queue = useRef<((files: File[], via: ReceiptVia) => void) | null>(null);
  // Changed on another device (or by someone else): shown here without a refresh.
  useLiveReport(report.id, { onChanged: onReload });
  const [editing, setEditing] = useState<ExpenseEntry>();
  // Set when a receipt was read but its date couldn't be found, so the edit sheet asks for it.
  const [askDate, setAskDate] = useState(false);
  const [editingReport, setEditingReport] = useState(false);
  const [moving, setMoving] = useState<ExpenseEntry>();
  const [review, setReview] = useState<Review>();
  const errorRef = useRef<HTMLDivElement>(null);
  const { data: pendingData, reload: reloadPending } = useApiQuery<ExpenseEntry[]>(
    report.status === "Draft" && report.submitter.id === user.id ? "/api/expenses/pending" : null,
  );
  const emailed = pendingData ?? [];
  const { data: myReports } = useApiQuery<ReportSummary[]>(
    report.status === "Draft" && report.submitter.id === user.id ? "/api/expenses/reports" : null,
  );
  const otherDrafts = (myReports ?? []).filter((r) => r.status === "Draft" && r.id !== report.id);

  const isOwner = report.submitter.id === user.id;
  const isManager = report.manager?.id === user.id;
  const isPayroll = user.roles.includes("Payroll");
  const executives = report.executiveApprovers ?? [];
  const isExecutive = executives.some(
    (e) => (e.email ?? "").toLowerCase() === user.userName.toLowerCase(),
  );
  const draft = report.status === "Draft" && isOwner;
  const sentBack = isSentBack(report);
  const [asking, setAsking] = useState(false);
  // Back to the list it was opened from, or where a report like this one is usually found.
  const cameFrom = (location.state as { from?: string } | null)?.from;
  const backTo =
    cameFrom && /^\/(all|inbox|payroll)?(\?|$)/.test(cameFrom)
      ? cameFrom
      : isOwner
        ? "/"
        : report.status === "Submitted" || (report.status === "ExecutiveReview" && isExecutive)
          ? "/inbox"
          : isPayroll
            ? "/payroll"
            : "/all";

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error]);

  function run(label: string, work: () => Promise<unknown>) {
    if (pending) return;
    setPending(label);
    setError(undefined);
    work()
      .then((result) => {
        if (result && typeof result === "object" && "entries" in result) {
          onReport(result as ReportDetail);
        } else {
          onReload();
        }
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : String(caught)),
      )
      .finally(() => setPending(undefined));
  }

  function added(updated: ReportDetail) {
    const before = new Set(report.entries.map((entry) => entry.id));
    onReport(updated);
    setAdding(undefined);
    // A receipt nobody could read arrives at zero, and one without a readable date is filed under
    // today. Ask while the paper is in hand.
    const fresh = updated.entries.find((entry) => !before.has(entry.id));
    if (fresh?.kind !== "Receipt") return;
    const noDate = !fresh.receipt?.receiptDate;
    if (fresh.amountUsd === 0 || noDate) {
      setAskDate(noDate);
      setEditing(fresh);
    }
  }

  // The rules on each expense's type, checked by the server: a block stops the report being
  // sent, a warning travels with it to the approvers.
  const catalog = useCatalog();
  const issues = report.issues ?? [];
  const blockers = new Set(issues.filter((i) => i.mode === "Block").map((i) => i.entryId));
  const flagged = new Set(issues.map((i) => i.entryId));
  const reviewNote = report.accountingComment ?? report.managerComment;
  // Where this report goes once the person a delegate prepared it for approves it.
  const sendsTo =
    user.approver && user.approver.id !== user.id ? user.approver.displayName : "payroll";

  return (
    <div className="report">
      <BackLink to={backTo} />

      <section className="card summary">
        <div className="row">
          <StatusBadge report={report} />
          <span className="summary-actions">
            {report.number ? <span className="report-id">Report #{report.number}</span> : null}
            {report.number && draft ? (
              <span className="report-id" aria-hidden="true">
                –
              </span>
            ) : null}
            {draft && (
              <button
                type="button"
                className="link-btn"
                onClick={() => setEditingReport(true)}
                aria-label="Edit report details"
              >
                <Icon name="pencil" size={16} /> Edit
              </button>
            )}
          </span>
        </div>
        <div className="summary-title">
          <h1>{report.title}</h1>
          <p className="muted">
            {dateRange(report.periodStart, report.periodEnd)}
            {report.purpose ? ` · ${report.purpose}` : ""}
          </p>
        </div>
        {!isOwner && <p className="muted">From {report.submitter.displayName}</p>}
        <div className="total">
          <span>{money(report.totalUsd)}</span>
          <span className="muted">
            {report.entries.length} expense{report.entries.length === 1 ? "" : "s"}
          </span>
        </div>
        {report.status === "Paid" && (report.reimbursementDate || isPayroll) && (
          <div className="reimbursed-row">
            {report.reimbursementDate && (
              <p className="reimbursed">
                <Icon name="pay" size={16} /> Reimbursement Date -{" "}
                {longDate(report.reimbursementDate)}
              </p>
            )}
            {isPayroll && (
              <button
                type="button"
                className="btn secondary small"
                disabled={pending !== undefined}
                onClick={() => setReview({ stage: "reopen", approve: false })}
              >
                Re-Open
              </button>
            )}
          </div>
        )}
      </section>

      {report.status === "DelegatorReview" && (
        <div className="note warn">
          <strong>
            {isOwner && !user.actingFor
              ? "Prepared for you"
              : `Waiting for ${report.submitter.displayName} to review`}
          </strong>
          <p>
            {report.preparedBy?.displayName ?? "Their delegate"} prepared this report
            {isOwner && !user.actingFor
              ? `. Send it to ${sendsTo} if it's right, or send it back with what to change.`
              : ` for ${report.submitter.displayName}, who approves it before it goes on.`}
          </p>
        </div>
      )}
      {report.status === "ExecutiveReview" && (
        <div className="note warn">
          <strong>Waiting on an executive</strong>
          <p>
            Payroll asked {executives.map((e) => e.displayName).join(" or ")} to approve this report
            before it is paid.
          </p>
        </div>
      )}
      {report.executiveComment &&
        report.status !== "ExecutiveReview" &&
        report.status !== "Paid" && (
          <div className="note">
            <strong>
              {report.status === "ExecutiveApproved"
                ? "Approved by an executive"
                : "Declined by an executive"}
            </strong>
            <p>{report.executiveComment}</p>
          </div>
        )}
      {isPayroll && report.status === "Paid" && (
        <ArchiveStatus
          report={report}
          busy={pending !== undefined}
          onRetry={() =>
            run("archive", () =>
              api.post<ReportDetail>(`/api/expenses/reports/${report.id}/payroll/archive`),
            )
          }
        />
      )}

      {reviewNote && (
        <div className={`note ${sentBack ? "danger" : ""}`}>
          <strong>
            {sentBack
              ? `Sent back by ${report.accountingComment ? "payroll" : (report.manager?.displayName ?? "your manager")}`
              : "Reviewer note"}
          </strong>
          <p>{reviewNote}</p>
        </div>
      )}

      {error && (
        <div className="error" role="alert" ref={errorRef}>
          {error}
        </div>
      )}

      {!draft && flagged.size > 0 && (
        <div className="note warn">
          <strong>
            {flagged.size === 1
              ? "1 expense is outside policy"
              : `${String(flagged.size)} expenses are outside policy`}
          </strong>
          <p>Open the flagged expenses below to see why.</p>
        </div>
      )}

      {draft && (
        <AddPanel
          forwardTo={user.forwardTo}
          pendingCount={emailed.length}
          onFiles={(files, via) => queue.current?.(files, via)}
          onManual={() => setAdding("manual")}
          onMileage={() => setAdding("mileage")}
          onPending={() => setAdding("pending")}
        />
      )}

      <section>
        <div className="section-head">
          <h2>Expenses</h2>
        </div>
        {draft && report.entries.length > 0 && (
          <p className="expenses-tip">
            <Icon name="pencil" size={15} />
            <span>
              If part of a receipt is personal, click on the Expense, click Edit, and enter it under
              Personal expense. It&apos;s taken off the amount.
            </span>
          </p>
        )}
        {draft && <ReceiptDropZone reportId={report.id} onAdded={onReload} queueRef={queue} />}

        {report.entries.length === 0 ? (
          <div className="card empty muted">
            {draft ? "Nothing on this report yet." : "No expenses on this report."}
          </div>
        ) : (
          <ul className="card entry-list">
            {report.entries.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                draft={draft}
                catalog={catalog}
                issues={issues.filter((i) => i.entryId === entry.id)}
                busy={pending !== undefined}
                onEdit={() => setEditing(entry)}
                onMove={() => setMoving(entry)}
                onRemove={() => {
                  void (async () => {
                    const yes = await dialogs.confirm({
                      title: `Remove ${entry.merchant}?`,
                      message: "It comes off this report.",
                      confirmLabel: "Remove",
                      danger: true,
                    });
                    if (!yes) return;
                    run("remove", () =>
                      api.del(`/api/expenses/reports/${report.id}/entries/${entry.id}`),
                    );
                  })();
                }}
              />
            ))}
          </ul>
        )}
      </section>

      <History events={report.events} />

      {draft && (
        <ActionBar>
          <div className="action-total">
            <span className="muted">Total</span>
            <strong>{money(report.totalUsd)}</strong>
          </div>
          <button
            className="btn"
            type="button"
            disabled={pending !== undefined || report.entries.length === 0 || blockers.size > 0}
            onClick={() =>
              run("submit", () =>
                api.post<ReportDetail>(`/api/expenses/reports/${report.id}/submit`),
              )
            }
          >
            {blockers.size === 0 && <Icon name="send" size={18} />}
            {pending === "submit"
              ? "Sending…"
              : blockers.size > 0
                ? `Fix ${blockers.size === 1 ? "1 item" : `${String(blockers.size)} items`} to send`
                : user.actingFor
                  ? `Send to ${user.displayName} to approve`
                  : `Send to ${user.approver?.displayName ?? "accounting"}`}
          </button>
        </ActionBar>
      )}

      {report.status === "DelegatorReview" && isOwner && !user.actingFor && (
        <ReviewBar
          approveLabel={`Send to ${sendsTo}`}
          busy={pending !== undefined}
          onApprove={() => setReview({ stage: "delegator", approve: true })}
          onReject={() => setReview({ stage: "delegator", approve: false })}
        />
      )}
      {report.status === "Submitted" && isManager && (
        <ReviewBar
          approveLabel="Approve"
          busy={pending !== undefined}
          onApprove={() => setReview({ stage: "manager", approve: true })}
          onReject={() => setReview({ stage: "manager", approve: false })}
        />
      )}
      {report.status === "ExecutiveReview" && isExecutive && (
        <ReviewBar
          approveLabel="Approve"
          rejectLabel="Decline"
          busy={pending !== undefined}
          onApprove={() => setReview({ stage: "executive", approve: true })}
          onReject={() => setReview({ stage: "executive", approve: false })}
        />
      )}
      {WithPayroll.includes(report.status) && isPayroll && (
        <PayrollBar
          report={report}
          busy={pending}
          onPaid={(date, note) => {
            void (async () => {
              const yes = await dialogs.confirm({
                title: `Mark ${money(report.totalUsd)} as paid?`,
                message:
                  `To ${report.submitter.displayName}, reimbursed ${longDate(date)}.` +
                  (note ? `\n\nNote: ${note}` : ""),
                confirmLabel: "Mark paid",
              });
              if (!yes) return;
              run("paid", () =>
                api.post<ReportDetail>(`/api/expenses/reports/${report.id}/payroll/paid`, {
                  reimbursementDate: date,
                  note: note || null,
                }),
              );
            })();
          }}
          onAsk={() => setAsking(true)}
          onSendBack={() => setReview({ stage: "payroll", approve: false })}
        />
      )}

      <Sheet open={asking} title="Ask an executive" onClose={() => setAsking(false)} confirmDiscard>
        {asking && (
          <AskExecutives
            onSubmit={(userNames, note) => {
              setAsking(false);
              run("ask", () =>
                api.post<ReportDetail>(`/api/expenses/reports/${report.id}/payroll/executives`, {
                  userNames,
                  note,
                }),
              );
            }}
          />
        )}
      </Sheet>

      <Sheet
        open={adding !== undefined}
        title={
          adding === "receipt"
            ? "Snap or Upload a Receipt"
            : adding === "mileage"
              ? "Mileage"
              : adding === "manual"
                ? "Manual entry"
                : adding === "pending"
                  ? "Emailed receipts"
                  : "Add to report"
        }
        onClose={() => setAdding(undefined)}
        confirmDiscard
      >
        {adding === "choose" && <AddChoices onPick={setAdding} pendingCount={emailed.length} />}
        {adding === "pending" && (
          <ul className="card entry-list">
            {emailed.map((entry) => (
              <PendingRow
                key={entry.id}
                entry={entry}
                onOpen={() => {
                  setAdding(undefined);
                  run("move", () =>
                    api
                      .post<ReportDetail>(`/api/expenses/pending/${entry.id}/move`, {
                        reportId: report.id,
                      })
                      .then((updated) => {
                        reloadPending();
                        return updated;
                      }),
                  );
                }}
              />
            ))}
          </ul>
        )}
        {adding === "receipt" && <ReceiptForm reportId={report.id} onSaved={added} />}
        {adding === "mileage" && policy && (
          <MileageForm
            reportId={report.id}
            rate={policy.mileageRateUsd}
            typedMilesAllowed={policy.mileageTypedMilesAllowed ?? true}
            notesRequired={policy.mileageNotesRequired ?? false}
            onSaved={added}
          />
        )}
        {adding === "manual" && (
          <ExpenseForm
            reportId={report.id}
            knownEntryIds={report.entries.map((entry) => entry.id)}
            canDeduct={user.deductsFromPaycheck ?? false}
            onSaved={added}
          />
        )}
      </Sheet>

      <Sheet
        open={editing !== undefined}
        title={
          editing?.kind === "Mileage"
            ? "Edit trip"
            : editing?.amountUsd === 0
              ? "What does the receipt say?"
              : askDate
                ? "When was this?"
                : "Edit expense"
        }
        onClose={() => {
          setEditing(undefined);
          setAskDate(false);
        }}
        confirmDiscard
      >
        {editing?.kind === "Mileage" && policy && (
          <MileageForm
            reportId={report.id}
            rate={policy.mileageRateUsd}
            typedMilesAllowed={policy.mileageTypedMilesAllowed ?? true}
            notesRequired={policy.mileageNotesRequired ?? false}
            entry={editing}
            onSaved={(updated) => {
              onReport(updated);
              setEditing(undefined);
            }}
          />
        )}
        {editing && editing.kind !== "Mileage" && (
          <>
            {askDate && (
              <div className="note warn">
                <strong>We couldn't find a date on the receipt.</strong>
                <p>Check the date below. It's set to today for now.</p>
              </div>
            )}
            {editing.receipt && <ReceiptPreview receipt={editing.receipt} />}
            <ExpenseForm
              reportId={report.id}
              entry={editing}
              canDeduct={user.deductsFromPaycheck ?? false}
              onSaved={(updated) => {
                onReport(updated);
                setEditing(undefined);
                setAskDate(false);
              }}
            />
          </>
        )}
      </Sheet>

      <Sheet
        open={moving !== undefined}
        title="Move to another report"
        onClose={() => setMoving(undefined)}
      >
        {moving && (
          <div className="stack">
            <p className="muted">
              {moving.merchant}, {money(moving.amountUsd)}. Choose where it belongs.
            </p>
            <div className="choices">
              {otherDrafts.map((draftReport) => (
                <button
                  key={draftReport.id}
                  type="button"
                  className="choice"
                  disabled={pending !== undefined}
                  onClick={() => {
                    const entry = moving;
                    setMoving(undefined);
                    run("move", () =>
                      api.post<ReportDetail>(
                        `/api/expenses/reports/${report.id}/entries/${entry.id}/move`,
                        { reportId: draftReport.id },
                      ),
                    );
                  }}
                >
                  <span className="choice-icon">
                    <Icon name="reports" size={22} />
                  </span>
                  <span>
                    <strong>{draftReport.title}</strong>
                    <span className="muted">
                      {dateRange(draftReport.periodStart, draftReport.periodEnd)}
                    </span>
                  </span>
                  <Icon name="chevronRight" size={18} />
                </button>
              ))}
              <button
                type="button"
                className="choice"
                disabled={pending !== undefined}
                onClick={() => {
                  const entry = moving;
                  setMoving(undefined);
                  run("move", () =>
                    api.post<ReportDetail>(
                      `/api/expenses/reports/${report.id}/entries/${entry.id}/move`,
                      { reportId: null },
                    ),
                  );
                }}
              >
                <span className="choice-icon">
                  <Icon name="mail" size={22} />
                </span>
                <span>
                  <strong>Pending</strong>
                  <span className="muted">Keep it aside and decide later</span>
                </span>
                <Icon name="chevronRight" size={18} />
              </button>
            </div>
            {otherDrafts.length === 0 && (
              <p className="muted">You have no other draft reports. Start one from Reports.</p>
            )}
          </div>
        )}
      </Sheet>

      <Sheet
        open={editingReport}
        title="Report details"
        onClose={() => setEditingReport(false)}
        confirmDiscard
      >
        <ReportDetailsForm
          report={report}
          onSaved={(updated) => {
            onReport(updated);
            setEditingReport(false);
          }}
          onDiscard={() => {
            void (async () => {
              const yes = await dialogs.confirm({
                title: "Discard this draft?",
                message: "Everything on it is deleted.",
                confirmLabel: "Discard",
                danger: true,
              });
              if (!yes) return;
              setEditingReport(false);
              setPending("discard");
              api
                .del(`/api/expenses/reports/${report.id}`)
                .then(() => navigate("/", { replace: true }))
                .catch((caught: unknown) => {
                  setError(caught instanceof Error ? caught.message : String(caught));
                  setPending(undefined);
                });
            })();
          }}
        />
      </Sheet>

      <Sheet
        open={review !== undefined}
        title={
          review?.approve
            ? review.stage === "delegator"
              ? `Send to ${sendsTo}`
              : "Approve report"
            : review?.stage === "executive"
              ? "Decline report"
              : review?.stage === "reopen"
                ? "Re-open report"
                : "Send back"
        }
        onClose={() => setReview(undefined)}
        confirmDiscard
      >
        {review && (
          <ReviewForm
            approve={review.approve}
            submitter={
              review.stage === "delegator"
                ? (report.preparedBy?.displayName ?? "your delegate")
                : report.submitter.displayName
            }
            approveLabel={review.stage === "delegator" ? `Send to ${sendsTo}` : "Approve"}
            declining={review.stage === "executive"}
            reopening={review.stage === "reopen"}
            onSubmit={(comment) => {
              const current = review;
              setReview(undefined);
              run(
                current.stage === "reopen" ? "reopen" : current.approve ? "approve" : "reject",
                () => api.post<ReportDetail>(reviewPath(report.id, current), { comment }),
              );
            }}
          />
        )}
      </Sheet>
    </div>
  );
}

const HistoryPageSize = 10;

/** The report's history, newest first, ten entries a page. */
function History({ events }: { events: ReportDetail["events"] }) {
  const [page, setPage] = useState(0);
  if (events.length === 0) return null;

  const newestFirst = [...events].reverse();
  const pages = Math.ceil(newestFirst.length / HistoryPageSize);
  const current = Math.min(page, pages - 1);
  const shown = newestFirst.slice(current * HistoryPageSize, (current + 1) * HistoryPageSize);

  return (
    <section>
      <h2>History</h2>
      <ol className="timeline">
        {shown.map((event) => (
          <li key={event.id}>
            <div>
              <strong>{eventLabel(event.kind, event.comment)}</strong>
              {eventNote(event.kind, event.comment) && (
                <p className="quote">“{eventNote(event.kind, event.comment)}”</p>
              )}
            </div>
            <span className="muted">
              {event.actorName} · {relativeTime(event.occurredAt)}
              {addedHow(event.via, event.device) && ` · ${addedHow(event.via, event.device)}`}
            </span>
          </li>
        ))}
      </ol>
      {pages > 1 && (
        <nav className="pager" aria-label="History pages">
          <button
            type="button"
            className="link-btn"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            <Icon name="chevronLeft" size={16} /> Newer
          </button>
          <span className="muted">
            Page {current + 1} of {pages}
          </span>
          <button
            type="button"
            className="link-btn"
            disabled={current === pages - 1}
            onClick={() => setPage(current + 1)}
          >
            Older <Icon name="chevronRight" size={16} />
          </button>
        </nav>
      )}
    </section>
  );
}

function backLabel(to: string): string {
  const path = to.split("?")[0];
  if (path === "/") return "My Reports";
  if (path === "/inbox") return "Approvals";
  if (path === "/payroll") return "Payroll";
  return "Search";
}

function BackLink({ to }: { to: string }) {
  return (
    <Link className="back" to={to}>
      <Icon name="chevronLeft" size={18} />
      {backLabel(to)}
    </Link>
  );
}

const choices: {
  step: Exclude<AddStep, "choose" | "pending">;
  icon: IconName;
  title: string;
  hint: string;
}[] = [
  {
    step: "receipt",
    icon: "camera",
    title: "Snap or Upload a Receipt",
    hint: "Photo, PDF or email",
  },
  { step: "mileage", icon: "car", title: "Mileage", hint: "Miles driven for work" },
  {
    step: "manual",
    icon: "pencil",
    title: "Manual entry",
    hint: "Type it in, and attach a receipt if you have one",
  },
];

function AddChoices({
  onPick,
  pendingCount,
}: {
  onPick: (step: AddStep) => void;
  pendingCount: number;
}) {
  const shown =
    pendingCount > 0
      ? [
          {
            step: "pending" as const,
            icon: "mail" as IconName,
            title: `Emailed receipts (${String(pendingCount)})`,
            hint: "Already read, waiting for a report",
          },
          ...choices,
        ]
      : choices;
  return (
    <div className="choices">
      {shown.map((choice) => (
        <button
          key={choice.step}
          type="button"
          className="choice"
          onClick={() => onPick(choice.step)}
        >
          <span className="choice-icon">
            <Icon name={choice.icon} size={22} />
          </span>
          <span>
            <strong>{choice.title}</strong>
            <span className="muted">{choice.hint}</span>
          </span>
          <Icon name="chevronRight" size={18} />
        </button>
      ))}
    </div>
  );
}

const kindIcon: Record<ExpenseEntry["kind"], IconName> = {
  Receipt: "receipt",
  Mileage: "car",
  Manual: "pencil",
};

/** The first letter in lower case, so a message reads on after "Click Edit to". */
const continuing = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

/**
 * For rules met by adding something, what Edit lets them do, read on from "Click Edit to":
 * "explain what monday.com was for (Other)." Undefined for rules Edit can't settle.
 */
function editAction(issue: PolicyIssue, merchant: string): string | undefined {
  switch (issue.rule) {
    case "Explanation":
    case "Amount":
    case "TripNote":
      return continuing(issue.message);
    case "Field": {
      // "Cafe Luna: list who else was there." reads as "list who else was there for Cafe Luna."
      const action = issue.message
        .slice(issue.message.indexOf(":") + 1)
        .trim()
        .replace(/\.$/, "");
      return `${continuing(action)} for ${merchant}.`;
    }
    case "Receipt":
      return `attach the receipt for ${merchant}.`;
    case "ReceiptDoubt": {
      // "Paychex Store might not be a receipt: No prices shown."
      const why = issue.message
        .slice(issue.message.indexOf(":") + 1)
        .trim()
        .replace(/\.$/, "");
      return `check it's a receipt (${why}), then save it. If it isn't one, remove it.`;
    }
    default:
      return undefined;
  }
}

/** A rule's name as a chip says it. */
const issueChip: Record<string, string> = {
  Amount: "Needs the Amount",
  Receipt: "Needs a Receipt",
  Explanation: "Needs an Explanation",
  Field: "Needs Details",
  PerPerson: "Over the per-person limit",
  Daily: "Over the daily allowance",
  PerDay: "Too many today",
  Tip: "Tip over the limit",
  OldReceipt: "Old receipt",
  OldTrip: "Old trip",
  TripNote: "Needs a Note",
  ReceiptDoubt: "Might not be a receipt",
};

function EntryRow({
  entry,
  draft,
  catalog,
  issues,
  busy,
  onEdit,
  onMove,
  onRemove,
}: {
  entry: ExpenseEntry;
  draft: boolean;
  catalog: Catalog | undefined;
  issues: PolicyIssue[];
  busy: boolean;
  onEdit: () => void;
  onMove: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  // The (i)s on Edit, Move and Remove (in dev, while the Tips switch is on).
  const help = useHelp();
  const showTips = useShowTips();
  const tips = devPreview && showTips;
  const [tipFor, setTipFor] = useState<HelpKey>();
  const actions: { key: HelpKey; label: string; icon: IconName; onClick: () => void }[] = [
    { key: "entryEdit", label: "Edit", icon: "pencil", onClick: onEdit },
    { key: "entryMove", label: "Move", icon: "send", onClick: onMove },
    { key: "entryRemove", label: "Remove", icon: "trash", onClick: onRemove },
  ];
  const shownAction = actions.find((action) => action.key === tipFor);
  // A trip's merchant is already its route, so the line under it says how far instead.
  const subtitle =
    entry.kind === "Mileage" && entry.mileage
      ? miles(entry.mileage.distanceMiles)
      : entryTypeLabel(entry, catalog);
  // Blocks first: they're what stands between the report and sending it.
  const ordered = [...issues].sort(
    (a, b) => Number(b.mode === "Block") - Number(a.mode === "Block"),
  );
  const first = ordered[0];

  return (
    <li className={`entry ${open ? "open" : ""}`}>
      <button
        type="button"
        className="entry-main"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="entry-icon">
          <Icon name={kindIcon[entry.kind]} />
        </span>
        <span className="entry-text">
          <strong>{entry.merchant}</strong>
          <span className="muted">
            {subtitle} · {shortDate(entry.incurredOn)}
            {entry.source === "Email" ? " · emailed" : ""}
          </span>
          {first && (
            <span className={`warn-chip ${first.mode === "Block" ? "block" : ""}`}>
              <Icon name="alert" size={14} /> {issueChip[first.rule] ?? "Outside policy"}
              {ordered.length > 1 ? ` +${String(ordered.length - 1)}` : ""}
            </span>
          )}
        </span>
        <span className={`entry-amount ${entry.amountUsd < 0 ? "refund" : ""}`}>
          {entry.amountUsd < 0 && (
            <span className="small">{entry.deductFromPaycheck ? "From paycheck" : "Refund"}</span>
          )}
          {money(entry.amountUsd)}
          {entry.currency !== "USD" && entry.originalAmount != null && (
            <span className="muted small">{money(entry.originalAmount, entry.currency)}</span>
          )}
        </span>
      </button>

      {open && (
        <div className="entry-detail">
          {ordered.length > 0 && (
            <ul className="issue-list">
              {ordered.map((issue) => (
                <li key={issue.rule} className={issue.mode === "Block" ? "block" : ""}>
                  <Icon name="alert" size={14} />
                  {draft && editAction(issue, entry.merchant) ? (
                    <span>
                      Click <strong>Edit</strong> to {editAction(issue, entry.merchant)}
                    </span>
                  ) : (
                    <span>
                      {issue.message}
                      {issue.mode === "Block" && draft ? " Fix it to send the report." : ""}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <TypeNotice type={findType(catalog, entry.expenseType)?.type} />
          <EntryFacts entry={entry} catalog={catalog} />
          {entry.mileage && (
            <p className="muted">
              {miles(entry.mileage.distanceMiles)} at {money(entry.mileage.rateUsd)} a mile
              {entry.mileage.calculationMethod === "Manual"
                ? ", miles typed in"
                : entry.mileage.calculationMethod === "AzureMaps"
                  ? ", by road"
                  : ", in a straight line"}
            </p>
          )}
          {entry.description && entry.description !== entry.merchant && (
            <p className="muted">{entry.description}</p>
          )}
          {entry.notes && <p className="muted">{entry.notes}</p>}
          {entry.receipt && (
            <>
              <ReceiptPreview receipt={entry.receipt} />
              {entry.receipt.lineItems.length > 0 && (
                <ul className="receipt-lines">
                  {entry.receipt.lineItems.map((line) => (
                    <li key={line.id} className="row">
                      <span>{line.description}</span>
                      <span>{money(line.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {draft && (
            <>
              <div className="entry-actions">
                {actions.map((action) => {
                  const button = (
                    <button
                      key={action.key}
                      type="button"
                      className={`btn ${action.key === "entryEdit" ? "secondary" : "ghost"} small`}
                      onClick={action.onClick}
                      disabled={busy}
                      aria-describedby={tips ? `${entry.id}-${action.key}` : undefined}
                    >
                      <Icon name={action.icon} size={16} /> {action.label}
                    </button>
                  );
                  return tips ? (
                    <CornerTip
                      key={action.key}
                      id={`${entry.id}-${action.key}`}
                      label={action.label}
                      tip={<HelpText text={help(action.key)} />}
                      open={tipFor === action.key}
                      onToggle={() => setTipFor(tipFor === action.key ? undefined : action.key)}
                    >
                      {button}
                    </CornerTip>
                  ) : (
                    button
                  );
                })}
              </div>
              {tips && shownAction && (
                <div className="add-tip-line" role="status">
                  <HelpText
                    text={help(shownAction.key)}
                    lead={<strong>{shownAction.label}:</strong>}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

/** The details an expense's type asked for: who was there, the client, the brand, the tip. */
function EntryFacts({ entry, catalog }: { entry: ExpenseEntry; catalog: Catalog | undefined }) {
  const facts: string[] = [];
  if (entry.fieldAnswer) {
    const prompt = findType(catalog, entry.expenseType)?.type.fieldPrompt;
    facts.push(prompt ? `${prompt} ${entry.fieldAnswer}` : entry.fieldAnswer);
  }
  if (entry.attendees) facts.push(`With ${nameList(entry.attendees)}`);
  if (entry.headcount) facts.push(`${String(entry.headcount)} people`);
  if (entry.days) facts.push(`${String(entry.days)} days`);
  if (entry.clientName) facts.push(`Client: ${entry.clientName}`);
  if (entry.brand) facts.push(`Brand: ${entry.brand}`);
  if (entry.tipAmount) facts.push(`Tip ${money(entry.tipAmount, entry.currency)}`);
  if (entry.personalAmount)
    facts.push(`${money(entry.personalAmount, entry.currency)} personal, taken off`);
  if (entry.currency !== "USD" && entry.originalAmount != null && entry.exchangeRate != null)
    facts.push(
      `${money(entry.originalAmount, entry.currency)} at ${money(entry.exchangeRate)} per ${entry.currency}`,
    );
  if (facts.length === 0) return null;
  return <p className="muted">{facts.join(" · ")}</p>;
}

function ActionBar({ children }: { children: ReactNode }) {
  return <div className="action-bar">{children}</div>;
}

function ReviewBar({
  approveLabel,
  busy,
  onApprove,
  onReject,
  rejectLabel = "Send back",
}: {
  approveLabel: string;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  rejectLabel?: string;
}) {
  return (
    <ActionBar>
      <button className="btn ghost danger-text" type="button" onClick={onReject} disabled={busy}>
        {rejectLabel}
      </button>
      <button className="btn" type="button" onClick={onApprove} disabled={busy}>
        <Icon name="approve" size={18} /> {approveLabel}
      </button>
    </ActionBar>
  );
}

/**
 * Payroll's bar: the reimbursement date, then pay it, ask an executive to approve it first, or
 * send it back.
 */
function PayrollBar({
  report,
  busy,
  onPaid,
  onAsk,
  onSendBack,
}: {
  report: ReportDetail;
  busy: string | undefined;
  onPaid: (date: string, note: string) => void;
  onAsk: () => void;
  onSendBack: () => void;
}) {
  // A re-opened report starts from the date it was paid with.
  const [date, setDate] = useState(report.reimbursementDate ?? todayIso());
  const [note, setNote] = useState("");
  return (
    <div className="action-bar payroll-bar">
      <div className="payroll-pay">
        <label>
          Reimbursement date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
        <button
          className="btn"
          type="button"
          disabled={busy !== undefined || date === ""}
          onClick={() => onPaid(date, note.trim())}
        >
          {busy === "paid" ? "Saving…" : `Mark paid · ${money(report.totalUsd)}`}
        </button>
      </div>
      <label className="payroll-note">
        Note <span className="optional">optional, shown in History and the email</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </label>
      <div className="payroll-more">
        <button
          className="btn ghost danger-text small"
          type="button"
          onClick={onSendBack}
          disabled={busy !== undefined}
        >
          Send back
        </button>
        <button
          className="btn secondary small"
          type="button"
          onClick={onAsk}
          disabled={busy !== undefined}
        >
          <Icon name="approve" size={16} /> Ask an executive
        </button>
      </div>
    </div>
  );
}

/** Payroll picks one or more executives from Settings; any one of them can approve. */
function AskExecutives({
  onSubmit,
}: {
  onSubmit: (userNames: string[], note: string | null) => void;
}) {
  const { data, error, loading } = useApiQuery<PersonBrief[]>("/api/expenses/executives");
  const [chosen, setChosen] = useState<string[]>([]);
  const [note, setNote] = useState("");

  if (loading && !data) return <div className="skeleton line wide" />;
  if (error) return <div className="error">{error.message}</div>;
  if (!data || data.length === 0)
    return (
      <p className="muted">
        No executives are set up yet. An admin can add them under Settings → Executive approvers.
      </p>
    );

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(chosen, note.trim() === "" ? null : note.trim());
      }}
    >
      <p className="muted">Any one of the people you pick can approve it.</p>
      {data.map((person) => {
        const userName = person.email ?? person.displayName;
        return (
          <label key={userName} className="check">
            <input
              type="checkbox"
              checked={chosen.includes(userName)}
              onChange={(e) =>
                setChosen((list) =>
                  e.target.checked ? [...list, userName] : list.filter((n) => n !== userName),
                )
              }
            />
            <span>
              {person.displayName}
              {person.displayName !== userName && (
                <span className="muted small"> · {userName}</span>
              )}
            </span>
          </label>
        );
      })}
      <label>
        Note <span className="optional">optional</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Why it needs an executive's approval"
        />
      </label>
      <div className="sheet-actions">
        <button className="btn" disabled={chosen.length === 0}>
          Send for approval
        </button>
      </div>
    </form>
  );
}

/** Whether a paid report reached the archive share, for payroll. */
function ArchiveStatus({
  report,
  busy,
  onRetry,
}: {
  report: ReportDetail;
  busy: boolean;
  onRetry: () => void;
}) {
  if (report.archivedAt && report.archivePath)
    return (
      <p className="muted small archive-status">
        <Icon name="check" size={14} /> Archived to {report.archivePath}
      </p>
    );
  if (report.archiveError)
    return (
      <div className="note danger">
        <strong>The archive copy failed</strong>
        <p>{report.archiveError}</p>
        <button type="button" className="btn small secondary" onClick={onRetry} disabled={busy}>
          Try again
        </button>
      </div>
    );
  return <p className="muted small archive-status">Saving to the archive…</p>;
}

function ReviewForm({
  approve,
  submitter,
  approveLabel,
  declining = false,
  reopening = false,
  onSubmit,
}: {
  approve: boolean;
  submitter: string;
  approveLabel: string;
  declining?: boolean;
  reopening?: boolean;
  onSubmit: (comment: string) => void;
}) {
  const [comment, setComment] = useState("");
  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(comment.trim());
      }}
    >
      <label>
        {approve ? (
          <>
            Note <span className="optional">optional</span>
          </>
        ) : declining ? (
          "Why are you declining it? Payroll will see this."
        ) : reopening ? (
          "Why are you re-opening it? This goes in the History."
        ) : (
          `What does ${submitter} need to fix?`
        )}
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          required={!approve}
          autoFocus
        />
      </label>
      {reopening && (
        <p className="muted">
          It goes back to the Payroll queue, where you can change the date and mark it paid again,
          send it back to {submitter}, or ask an executive.
        </p>
      )}
      <div className="sheet-actions">
        <button className={`btn ${approve || reopening ? "" : "danger"}`}>
          {approve ? approveLabel : declining ? "Decline" : reopening ? "Re-open" : "Send back"}
        </button>
      </div>
    </form>
  );
}

function ReportDetailsForm({
  report,
  onSaved,
  onDiscard,
}: {
  report: ReportDetail;
  onSaved: (report: ReportDetail) => void;
  onDiscard: () => void;
}) {
  const api = useApi();
  const [title, setTitle] = useState(report.title);
  const [purpose, setPurpose] = useState(report.purpose ?? "");
  const [start, setStart] = useState(report.periodStart);
  const [end, setEnd] = useState(report.periodEnd);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(undefined);
        api
          .patch<ReportDetail>(`/api/expenses/reports/${report.id}`, {
            title,
            purpose: purpose.trim() === "" ? null : purpose,
            periodStart: start,
            periodEnd: end,
          })
          .then(onSaved)
          .catch((caught: unknown) => {
            setError(caught instanceof Error ? caught.message : String(caught));
            setBusy(false);
          });
      }}
    >
      <label>
        Trip or event
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
      </label>
      <label>
        Purpose <span className="optional">optional</span>
        <input value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      </label>
      <div className="split">
        <label>
          Starts
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
        </label>
        <label>
          Ends
          <input
            type="date"
            value={end}
            min={start}
            onChange={(e) => setEnd(e.target.value)}
            required
          />
        </label>
      </div>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <div className="sheet-actions">
        <button className="btn" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      <button type="button" className="btn ghost danger-text" onClick={onDiscard}>
        <Icon name="trash" size={16} /> Discard draft
      </button>
    </form>
  );
}

function ReportSkeleton() {
  return (
    <div className="report" aria-busy="true" aria-label="Loading report">
      <div className="skeleton line short" />
      <div className="card summary">
        <div className="skeleton line short" />
        <div className="skeleton line" />
        <div className="skeleton line wide" />
      </div>
      <div className="card">
        <div className="skeleton line" />
        <div className="skeleton line" />
      </div>
    </div>
  );
}
